import * as RadixPopover from '@radix-ui/react-popover';
import { type MouseEvent, memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';
import { Command } from '@air/cmdk';

import { type SearchCommandListStartHelpers } from '~/components/SearchCommand/SearchCommand';
import { SearchCommandBoardList } from '~/components/SearchCommand/SearchCommandBoardList';
import {
  SearchCommandClearButton,
  type SearchCommandClearButtonProps,
} from '~/components/SearchCommand/SearchCommandClearButton';
import { SearchCommandInput } from '~/components/SearchCommand/SearchCommandInput';
import { SearchCommandSavedFilterList } from '~/components/SearchCommand/SearchCommandSavedFilterList';
import { SearchCommandSuggestionList } from '~/components/SearchCommand/SearchCommandSuggestionList';
import { SearchCommandPopoverTrigger } from '~/components/SearchCommand/SearchCommandTrigger';

export type MobileSearchCommandProps = {
  defaultOpen?: boolean;
  filtersCount?: number;
  hasFilterBar?: boolean;
  onBoardClick?: (board: SearchBoardsResponse) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onSavedFilterSelect?: (savedFilter: SearchSavedFiltersResponse) => void;
  onSearchChange?: (search: string) => void;
  onSuggestionClick?: (suggestion: string) => void;
  placeholder?: string;
  search?: string;
  suggestions?: SuggestionsResponse;
  onClear?: SearchCommandClearButtonProps['onClick'];
  renderListStart?: (helpers: SearchCommandListStartHelpers) => ReactNode;
};

export const MobileSearchCommand = memo(
  ({
    defaultOpen,
    filtersCount,
    onBoardClick: _onBoardClick,
    onClear,
    onOpenChange,
    onSavedFilterSelect: _onSavedFilterSelect,
    onSearchChange,
    onSuggestionClick: _onSuggestionClick,
    placeholder,
    search,
    suggestions,
    renderListStart,
  }: MobileSearchCommandProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
    const [searchValue, setSearchValue] = useState(search ?? '');
    const inputRef = useRef<HTMLInputElement>(null);
    const hasTerm = !!searchValue;
    const showClosedClear = !!onClear && hasTerm && !isOpen;

    useEffect(() => {
      setSearchValue(search ?? '');
    }, [search]);

    const onOpen = useCallback(() => {
      setIsOpen(true);
      onOpenChange?.(true);
    }, [onOpenChange]);

    const onBoardClick = useCallback(
      (board: SearchBoardsResponse) => {
        setSearchValue('');
        onSearchChange?.('');
        setIsOpen(false);
        _onBoardClick?.(board);
      },
      [_onBoardClick, onSearchChange],
    );

    const onSavedFilterSelect = useCallback(
      (savedFilter: SearchSavedFiltersResponse) => {
        setIsOpen(false);
        _onSavedFilterSelect?.(savedFilter);
      },
      [_onSavedFilterSelect],
    );

    const onSuggestionClick = useCallback(
      (suggestion: string) => {
        setIsOpen(false);
        setSearchValue(suggestion);
        _onSuggestionClick?.(suggestion);
      },
      [_onSuggestionClick],
    );

    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onSearchChange?.(value);
      },
      [onSearchChange],
    );

    const handleClear = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setSearchValue('');
        onSearchChange?.('');
        // Do not close the popover here. On board, search lives in MobileTopbar's overlay;
        // closing the popover first reveals the trigger ("another search"). Parent onClear
        // clears `q` and closeMobileSearch() unmounts the overlay.
        onClear?.(event);
      },
      [onClear, onSearchChange],
    );

    const handleCloseSearchCommand = useCallback(() => {
      setSearchValue('');
      onSearchChange?.('');
      setIsOpen(false);
    }, [onSearchChange]);

    const handleDismiss = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onClear?.(event);
        setIsOpen(false);
      },
      [onClear],
    );

    const handleOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
      inputRef.current?.focus();
    }, []);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setIsOpen(open);
        onOpenChange?.(open);
      },
      [onOpenChange],
    );

    return (
      <Command className="w-full" data-testid="SEARCH_COMMAND" shouldFilter={false} defaultSearch={search}>
        <RadixPopover.Root modal onOpenChange={handleOpenChange} open={isOpen}>
          <div className="relative">
            <RadixPopover.Trigger asChild>
              <SearchCommandPopoverTrigger
                count={filtersCount}
                onClick={onOpen}
                placeholder={placeholder}
                search={searchValue}
              />
            </RadixPopover.Trigger>
            {showClosedClear && <SearchCommandClearButton onClick={handleClear} />}
          </div>

          <RadixPopover.Portal>
            <RadixPopover.Content
              className="data-[state=closed]:fade-out data-[state=open]:fade-in pointer-events-none relative top-[calc(var(--radix-popover-trigger-height)*-1)] left-0 flex h-[calc(var(--radix-popover-content-available-height)+var(--radix-popover-trigger-height))] w-screen flex-col overflow-hidden transition-all data-[state=closed]:animate-out data-[state=open]:animate-in"
              data-testid="SEARCH_COMMAND_CONTENT"
              onOpenAutoFocus={handleOpenAutoFocus}
            >
              <div className="pointer-events-auto flex flex-col border-b ads:border-b-ads-gray-5 border-b-gray-5 ads:bg-base bg-gray-1 px-4 pb-2">
                <div className="relative w-full">
                  <SearchCommandInput
                    className="m-0 rounded-8 border ads:border-input border-gray-5"
                    count={filtersCount}
                    onValueChange={onValueChange}
                    placeholder={placeholder}
                    ref={inputRef}
                    value={searchValue}
                  />
                  {hasTerm ? (
                    !!onClear && <SearchCommandClearButton onClick={handleClear} />
                  ) : (
                    <SearchCommandClearButton label="Close search" onClick={handleDismiss} />
                  )}
                </div>
              </div>
              <div className="pointer-events-auto grow overflow-y-auto ads:bg-raised bg-gray-2">
                {isOpen && (
                  <Command.List className="flex flex-col gap-3 px-4 py-3" data-testid="SEARCH_COMMAND_LIST">
                    {!!_onSuggestionClick && (
                      <SearchCommandSuggestionList
                        onSelect={onSuggestionClick}
                        suggestions={suggestions?.suggestions}
                      />
                    )}
                    {renderListStart?.({ onClose: handleCloseSearchCommand })}

                    {!!suggestions?.savedFilters.length && !!onSavedFilterSelect && (
                      <SearchCommandSavedFilterList
                        onSelect={onSavedFilterSelect}
                        savedFilters={suggestions.savedFilters}
                      />
                    )}

                    {!!suggestions?.boards.length && (
                      <SearchCommandBoardList boards={suggestions.boards} onBoardClick={onBoardClick} />
                    )}
                  </Command.List>
                )}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      </Command>
    );
  },
);

MobileSearchCommand.displayName = 'MobileSearchCommand';
